import { useEffect } from 'react';
import { parseAsString, useQueryState } from 'nuqs';

export const FOCUS_PARAM = 'focus';
export const FOCUS_CONTACTS = 'contacts';
export const CONTACTS_MODULE_TESTID = 'account-contacts-module';

/**
 * `?focus=contacts` ("View contacts" on the hierarchy tab) is a DOM scroll-to-roster hint: once the
 * drilldown body is mounted (`ready`), scroll the Contacts module into view, then strip the param
 * so a reload does not repeat it. It never picks a tab: the tab is `?accountTab=` (`useAccountTab`).
 * `?focus=opps` is retired (producers send `?accountTab=opps`). Read/cleared through nuqs, shared
 * with ContactFlowTable's `?contact=`, which is preserved.
 *
 * StrictMode: no lifetime refs. The effect is idempotent (scrolling twice is harmless; deleting an
 * already-deleted param is a no-op) and does nothing once the param is gone.
 */
export function useFocusParam(ready: boolean): void {
  const [focus, setFocus] = useQueryState(FOCUS_PARAM, parseAsString.withOptions({ history: 'replace' }));
  useEffect(() => {
    if (!ready || focus !== FOCUS_CONTACTS) return;
    document.querySelector(`[data-testid="${CONTACTS_MODULE_TESTID}"]`)?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
    void setFocus(null);
  }, [ready, focus, setFocus]);
}
